/**
 * Podcasts main view component - with RSS feed management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useToast, ToastContainer } from '../common/Toast';
import podcastService from '../../services/podcastService';
import PodcastDetailView from './PodcastDetailView';

const PodcastsView = () => {
  const { state } = useAppContext();
  const { toasts, showToast, removeToast } = useToast();
  
  // Local state
  const [podcasts, setPodcasts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [refreshingStatus, setRefreshingStatus] = useState({});

  // Load podcasts
  const loadPodcasts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await podcastService.getPodcasts({ limit: 100, skip: 0 });
      console.log('Load podcasts response:', response);
      
      if (response.success) {
        const podcastsList = response.podcasts || response.data?.podcasts || [];
        const total = response.total || response.data?.total || 0;
        
        console.log('Podcasts found:', podcastsList.length, 'Total:', total);
        setPodcasts(podcastsList);
        setTotalCount(total);
      } else {
        console.error('Failed to load podcasts:', response);
        setPodcasts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error loading podcasts:', error);
      setPodcasts([]);
      setTotalCount(0);  
    } finally {
      setLoading(false);
    }
  }, []);

  // Load podcasts on mount
  useEffect(() => {
    loadPodcasts();
  }, [loadPodcasts]);

  // Handle add podcast
  const handleAddPodcast = useCallback(async (podcastData) => {
    try {
      showToast('Adding podcast...', 'info');
      
      const response = await podcastService.createPodcast(podcastData);
      
      console.log('Add podcast response:', response);
      
      if (response && (response.success !== false)) {
        showToast('✅ Podcast added successfully!', 'success');
        setAddModalOpen(false);
        loadPodcasts(); // Refresh list
        
        // Automatically refresh episodes for new podcast
        if (response.id) {
          setTimeout(() => {
            handleRefreshEpisodes(response.id);
          }, 1000);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Unknown error';
        console.error('Add podcast failed:', errorMsg);
        showToast(`❌ Failed to add podcast: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error adding podcast:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Unknown error';
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 422) {
          if (data.detail && Array.isArray(data.detail)) {
            const validationErrors = data.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
            errorMessage = `Validation error: ${validationErrors}`;
          } else if (data.detail) {
            errorMessage = `Validation error: ${data.detail}`;
          } else {
            errorMessage = 'Form validation failed';
          }
        } else if (status === 400 && data.detail && data.detail.includes('RSS URL already exists')) {
          errorMessage = 'A podcast with this RSS URL is already subscribed';
        } else {
          errorMessage = data?.detail || data?.message || `Server error: ${status}`;
        }
      } else if (error.request) {
        errorMessage = 'No response from server - check connection';
      } else {
        errorMessage = error.message || 'Request setup error';
      }
      
      showToast(`❌ Add podcast error: ${errorMessage}`, 'error');
    }
  }, [loadPodcasts, showToast]);

  // Handle refresh episodes
  const handleRefreshEpisodes = useCallback(async (podcastId, forceRefresh = false) => {
    try {
      setRefreshingStatus(prev => ({ ...prev, [podcastId]: true }));
      showToast('Refreshing episodes from RSS feed...', 'info');
      
      const response = await podcastService.refreshEpisodes(podcastId, forceRefresh);
      
      if (response && response.success) {
        const newEpisodes = response.new_episodes || response.data?.new_episodes || 0;
        const totalEpisodes = response.total_episodes || response.data?.total_episodes || 0;
        
        if (newEpisodes > 0) {
          showToast(`✅ Found ${newEpisodes} new episodes!`, 'success');
        } else {
          showToast(`✅ Episodes refreshed - ${totalEpisodes} total episodes, no new episodes found`, 'success');
        }
        
        loadPodcasts(); // Refresh list to show updated counts
      } else {
        const errorMsg = response?.error || response?.message || 'Refresh failed';
        showToast(`❌ Refresh failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error refreshing episodes:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Refresh error: ${errorMessage}`, 'error');
    } finally {
      setRefreshingStatus(prev => ({ ...prev, [podcastId]: false }));
    }
  }, [loadPodcasts, showToast]);

  // Handle view podcast details
  const handleViewPodcast = (podcast) => {
    setSelectedPodcast(podcast);
  };

  // Handle back from detail view
  const handleBackFromDetail = () => {
    setSelectedPodcast(null);
    loadPodcasts(); // Refresh list when returning
  };

  // Handle podcast update from detail view
  const handlePodcastUpdate = (updatedPodcast) => {
    setPodcasts(prev => prev.map(podcast => 
      podcast.id === updatedPodcast.id ? updatedPodcast : podcast
    ));
  };

  // If viewing a specific podcast, show detail view
  if (selectedPodcast) {
    return (
      <PodcastDetailView
        podcast={selectedPodcast}
        onBack={handleBackFromDetail}
        onUpdate={handlePodcastUpdate}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Podcasts</h1>
            <p className="text-sm text-gray-600">
              Subscribe to podcast RSS feeds and manage episodes for article generation
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setAddModalOpen(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Podcast</span>
            </button>
            <button
              onClick={loadPodcasts}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading podcasts...</p>
            </div>
          </div>
        ) : podcasts && podcasts.length > 0 ? (
          <div className="h-full flex flex-col">
            {/* Podcast List Header */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center text-xs font-medium text-gray-700">
              <div className="w-4 flex-shrink-0 mr-3"></div>
              <div className="w-8 flex-shrink-0 mr-3"></div>
              <div className="flex-1 min-w-0 mr-4">Podcast Name</div>
              <div className="w-24 text-center flex-shrink-0 mr-4">Status</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Episodes</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Transcribed</div>
              <div className="w-24 text-center flex-shrink-0 mr-4">Last Refresh</div>
              <div className="w-32 text-center flex-shrink-0">Action</div>
            </div>
            
            {/* Podcast Rows */}
            <div className="flex-1 overflow-auto bg-white">
              {podcasts.map((podcast) => (
                <div key={podcast.id} className="px-4 py-3 border-b border-gray-200 flex items-center hover:bg-gray-50">
                  {/* Checkbox */}
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300 flex-shrink-0 mr-3" />
                  
                  {/* Podcast Icon */}
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3">
                    {podcast.podcast_image_url ? (
                      <img src={podcast.podcast_image_url} alt="" className="w-8 h-8 rounded object-cover" />
                    ) : (
                      <span className="text-xl">🎧</span>
                    )}
                  </div>
                  
                  {/* Name Column */}
                  <div className="flex-1 min-w-0 mr-4">
                    <div 
                      className="text-sm font-semibold text-blue-600 hover:text-blue-800 cursor-pointer truncate"
                      onClick={() => handleViewPodcast(podcast)}
                    >
                      {podcast.podcast_name}
                    </div>
                    <div className="text-xs text-gray-600 truncate">
                      {podcast.podcast_title || 'No title'} • {podcast.description?.substring(0, 50)}...
                    </div>
                  </div>
                  
                  {/* Status Column */}
                  <div className="w-24 flex-shrink-0 mr-4 text-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      podcast.status === 'active' 
                        ? 'bg-green-100 text-green-800'
                        : podcast.status === 'error' 
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {podcast.status || 'pending'}
                    </span>
                  </div>
                  
                  {/* Episodes Count */}
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    <div className="text-sm font-medium text-gray-900">
                      {podcast.total_episodes || 0}
                    </div>
                  </div>
                  
                  {/* Transcribed Count */}
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    <div className="text-sm font-medium text-gray-900">
                      {podcast.transcribed_episodes || 0}
                    </div>
                  </div>
                  
                  {/* Last Refresh */}
                  <div className="w-24 text-center flex-shrink-0 mr-4">
                    <div className="text-xs text-gray-500">
                      {podcast.last_successful_refresh 
                        ? new Date(podcast.last_successful_refresh).toLocaleDateString()
                        : 'Never'
                      }
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  <div className="w-32 flex items-center justify-center space-x-2 flex-shrink-0">
                    <button 
                      onClick={() => handleRefreshEpisodes(podcast.id)}
                      disabled={refreshingStatus[podcast.id]}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:bg-blue-400 flex items-center space-x-1"
                    >
                      {refreshingStatus[podcast.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                          <span>Refreshing...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span>Refresh</span>
                        </>
                      )}
                    </button>
                    <button 
                      onClick={() => handleViewPodcast(podcast)}
                      className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Results Summary */}
            <div className="px-4 py-2 bg-white border-t border-gray-200">
              <div className="text-xs text-gray-700">
                Showing {podcasts.length} of {totalCount} podcasts
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <svg className="w-16 h-16 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15.536 6.464a5 5 0 010 7.072M12 9v6m3 0a9 9 0 01-6 0m6 0v1a2 2 0 01-2 2H9a2 2 0 01-2-2v-1m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v0" />
              </svg>
              <h3 className="text-lg font-medium mb-2">No podcasts found</h3>
              <p className="text-center mb-4">Get started by subscribing to your first podcast RSS feed.</p>
              
              <button
                onClick={() => setAddModalOpen(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
              >
                Add Your First Podcast
              </button>
              
              <div className="bg-white border border-gray-200 p-4 rounded-lg max-w-md mx-auto mt-6">
                <h4 className="font-medium mb-2">Podcast Features:</h4>
                <div className="text-left text-xs space-y-1">
                  <p>✓ RSS feed subscription</p>
                  <p>✓ Automatic episode discovery</p>
                  <p>✓ Audio transcription with OpenAI Whisper</p>
                  <p>✓ Article generation from episodes</p>
                  <p>✓ Same workflow as Interviews</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Podcast Modal */}
      {addModalOpen && (
        <AddPodcastModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onAdd={handleAddPodcast}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

// Add Podcast Modal Component
const AddPodcastModal = ({ isOpen, onClose, onAdd }) => {
  const [podcastName, setPodcastName] = useState('');
  const [rssUrl, setRssUrl] = useState('');
  const [description, setDescription] = useState('');
  const [autoTranscribe, setAutoTranscribe] = useState(true);
  const [maxEpisodes, setMaxEpisodes] = useState(10);
  const [notes, setNotes] = useState('');
  
  const [adding, setAdding] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!podcastName || !rssUrl) {
      return;
    }

    // Validate RSS URL
    if (!podcastService.validateRSSUrl(rssUrl)) {
      alert('Please enter a valid RSS URL (must start with http:// or https://)');
      return;
    }

    setAdding(true);
    try {
      await onAdd({
        podcast_name: podcastName,
        rss_url: rssUrl,
        description: description || undefined,
        auto_transcribe: autoTranscribe,
        max_episodes: maxEpisodes,
        notes: notes || undefined
      });
    } finally {
      setAdding(false);
    }
  };

  const handleRssUrlChange = (e) => {
    const url = e.target.value;
    setRssUrl(url);
    
    // Auto-populate podcast name from URL if empty
    if (url && !podcastName) {
      try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname.replace('www.', '');
        setPodcastName(domain);
      } catch {
        // Invalid URL, ignore
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Add Podcast</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={adding}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RSS Feed URL *
            </label>
            <input
              type="url"
              value={rssUrl}
              onChange={handleRssUrlChange}
              placeholder="https://example.com/podcast/feed"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              required
              disabled={adding}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the RSS feed URL for the podcast
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Podcast Name *
            </label>
            <input
              type="text"
              value={podcastName}
              onChange={(e) => setPodcastName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Enter a name for this podcast"
              required
              disabled={adding}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Brief description of this podcast"
              disabled={adding}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Episodes
              </label>
              <select
                value={maxEpisodes}
                onChange={(e) => setMaxEpisodes(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                disabled={adding}
              >
                <option value={5}>5 episodes</option>
                <option value={10}>10 episodes</option>
                <option value={20}>20 episodes</option>
                <option value={50}>50 episodes</option>
              </select>
            </div>
            
            <div className="flex items-center mt-6">
              <input
                type="checkbox"
                id="autoTranscribe"
                checked={autoTranscribe}
                onChange={(e) => setAutoTranscribe(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 mr-2"
                disabled={adding}
              />
              <label htmlFor="autoTranscribe" className="text-sm text-gray-700">
                Auto-transcribe episodes
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Any additional notes about this podcast"
              disabled={adding}
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              disabled={adding}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors text-sm flex items-center justify-center"
              disabled={adding || !podcastName || !rssUrl}
            >
              {adding ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                'Add Podcast'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PodcastsView;